import React, { useEffect, useState, useMemo } from "react";
import { set, useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/layout";
import Permission from "../../_config/Permission";
import { useMenuPermissionsSelector } from "../../store/selectors/userSelector";
import Header from "../../components/header";
import SubHeader from "../../components/SubHeader";
import SiteButtons from "../../components/SiteButtons";
import Container from "../../components/Container";
import BodyGroupHeading from "../../components/BodyGroupHeading";
import ReusableTable from "../../components/ReusableTable";
import Icon from "../../components/icons";
import Pagination from "../../components/pagination";
import SiteModal from "../../components/SiteModal";
import { useDiscountsApi } from "../../store/api/hooks/discountsApiHook";
import { URLS } from "../../_config";
import toast from "react-hot-toast";
import { useLookup, getLookupDisplayValue, getCustomerGroupDisplayName } from "../../_common/hookFunction";
import Input from "../../components/elements/form/input";
import CommonDropdown from "../../components/elements/form/commonDropdown";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useCommonApi } from "../../store/api/hooks/commonApiHook";
import { useDialog } from "../../store/api/hooks/dialogHook";
import TopTabs from "../../components/topTabs";
import moment from "moment";
import { useTranslation } from "react-i18next";
import FormattedDate from "../../components/elements/FormattedDate";

type FormValues = {
  keyword: string;
  createdDateFrom: Date | null;
  createdDateTo: Date | null;
  locationIds: number[];
};

const Discounts: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("discounts");
  const { confirm } = useDialog();
  const discountApi = useDiscountsApi();
  const commonApi = useCommonApi();
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    getValues,
    formState: { errors },
    trigger,
  } = useForm<FormValues>({
    defaultValues: {
      keyword: "",
      createdDateFrom: null,
      createdDateTo: null,
      locationIds: [],
    },
  });

  // Validation messages (rules={{ minLength: { message: t(...) } }}) are resolved once, at the
  // moment validation runs, and cached as plain strings in react-hook-form's error
  // state -- they don't re-render on their own. Re-validate any already-errored
  // fields on a language switch so their messages catch up to the new language.
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      trigger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const [showAddDiscountModal, setShowAddDiscountModal] =
    useState<boolean>(false);
  const [itemForEdit, setItemForEdit] = useState<any>(null);
  const [discountsData, setDiscountsData] = useState<any>(null);
  const [searching, setSearching] = useState<boolean>(false);
  const [createdDateRange, setCreatedDateRange] = useState<
    [Date | null, Date | null]
  >([null, null]);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [activeTab, setActiveTab] = useState("All");
  const lookup = useLookup();
  const daysOfWeek = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

  // Get permissions from Redux store based on current page URL using useMemo for optimization
  const allPermissions = useMenuPermissionsSelector();
  const permissionData = useMemo(() => 
    allPermissions?.[URLS.DISCOUNT] || null, 
    [allPermissions]
  );

  // Create ref to programmatically submit the form
  const formRef = React.useRef<HTMLFormElement>(null);
  // Track if we have active filters to avoid unnecessary form submission
  const [hasActiveFilters, setHasActiveFilters] = useState<boolean>(false);

  const tabs = [
    { name: "Current and future", label: t("list.tabs.currentAndFuture"), disabled: false },
    { name: "Past", label: t("list.tabs.past"), disabled: false },
    { name: "All", label: t("list.tabs.all"), disabled: false },
  ];

  const searchDiscounts = async (
    data?: any,
    resetPagination: boolean = false
  ) => {
    try {
      // If it's a search/filter operation, reset to page 1
      const currentPageToUse = resetPagination ? 1 : currentPage;

      const params = {
        ...data,
        pageNumber: currentPageToUse,
        pageSize: pageSize,
      };

      // Update current page state if it was reset
      if (resetPagination && currentPage !== 1) {
        setCurrentPage(1);
      }

      if (data) {
        setSearching(true);
      }
      setDiscountsData(null);

      const response: any = await new Promise((resolve, reject) => {
        discountApi.searchDiscount(params, resolve, reject);
      });

      if (
        response &&
        response?.data &&
        response?.success &&
        response.success === true &&
        response.data?.items &&
        Array.isArray(response.data.items)
      ) {
        const mappedDiscounts = response.data?.items?.map(
          (discount: any, index: number) => ({
            ...discount,
            id: discount.discountId || `discount-${index}`,
            level: 0,
            hasChildren: false,
            expanded: false,
            parentId: null,
            isDragging: false,
            isDragOver: false,
          })
        );
        if (mappedDiscounts?.length > 0) {
          setDiscountsData(mappedDiscounts);
        } else {
          setDiscountsData([]);
        }
        setTotalRows(response.data.totalRows || 0);
      } else {
        setDiscountsData([]);
        setTotalRows(response.data?.totalRows || 0);
      }
      setSearching(false);
    } catch (error) {
      console.log("Error searching discounts:", error);
      setSearching(false);
      setDiscountsData([]);
      setTotalRows(0);
    }
  };

  const resetSearch = () => {
    reset();
    
    // Maintain tab-specific date ranges when resetting
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    let newDateRange: [Date | null, Date | null] = [null, null];
    
    switch (activeTab) {
      case "Current and future":
        newDateRange = [null, null];
        setValue("createdDateFrom", null);
        setValue("createdDateTo", null);
        break;
      case "Past":
        newDateRange = [null, null];
        setValue("createdDateFrom", null);
        setValue("createdDateTo", null);
        break;
      case "All":
      default:
        newDateRange = [null, null];
        setValue("createdDateFrom", null);
        setValue("createdDateTo", null);
        break;
    }
    
    setCreatedDateRange(newDateRange);
    setHasActiveFilters(false);
    
    // Format date without timezone conversion
    
    const formatDateForAPI = (date: Date | null, isEnd: boolean = false) => {
      if (!date) return undefined;
        if (isEnd) {
            return (
              moment(date).endOf("day").utc().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z"
            );
          }
          return (
            moment(date).startOf("day").utc().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z"
          );
        };

    // Prepare search data with tab-specific date range
    const searchData = {
      searchKey: undefined,
      startDate: newDateRange[0] ? formatDateForAPI(newDateRange[0]) : undefined,
      endDate: newDateRange[1] ? formatDateForAPI(newDateRange[1], true) : undefined,
      locationIds: [],
      dateRangeType: activeTab === "Past" ? "Past" : activeTab === "Current and future" ? "CurrentAndFuture" : null,
    };

    searchDiscounts(searchData, true);
  };

  const confirmDelete = async (discount: any) => {
    try {
      const response: any = await new Promise((resolve, reject) => {
        discountApi.deleteDiscount(
          { discountId: discount.discountId },
          resolve,
          reject
        );
      });
      if (!response?.success) {
        throw new Error(response?.errorMessage || t("list.toasts.deleteFailed"));
      }
      toast.success(t("list.toasts.deleteSuccess"));
      if (totalRows % pageSize === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
        return;
      }
      if (hasActiveFilters && formRef.current) {
        handleSubmit((data) => handleFormSubmit(data, true))();
      } else {
        searchDiscounts();
      }
    } catch (error) {
      console.log("Error deleting discount:", error);
      toast.error((error as any) || t("list.toasts.deleteFailed"));
    }
  };

  const getDiscountType = (discount: any) => {
    const type: any = lookup.getDiscountMode()?.find((lt: any) => {
      return lt.lookupId == atob(discount?.fkDiscountTypeLookupValueId);
    });
    return type?.lookupValue?.toLowerCase() || "product";
  };

  const handleEdit = (discount: any) => {
    // const discountType = getDiscountType(discount);
    // console.log("Editing discount type:", discountType);
    // console.log(discount)
    navigate(URLS.EDIT_DISCOUNT(discount?.fkDiscountTypeLookupValueId, discount.discountId));
  };

  const onSubmit = (data: FormValues) => {
    handleFormSubmit(data, false);
  };

  const handleFormSubmit = (data: FormValues, fromPagination: boolean = false) => {
    if (data.keyword && data.keyword.length < 3) {
      toast.error(t("list.validation.searchMinChars"));
      return;
    }
    if (
      createdDateRange[0] &&
      createdDateRange[1] &&
      createdDateRange[0] > createdDateRange[1]
    ) {
      toast.error(t("list.validation.createdFromAfterTo"));
      return;
    }
    if (createdDateRange[1] && !createdDateRange[0]) {
      toast.error(t("list.validation.selectCreatedFrom"));
      return;
    }
    if (!createdDateRange[1] && createdDateRange[0]) {
      toast.error(t("list.validation.selectCreatedTo"));
      return;
    }
    // Format date without timezone conversion
    
    const formatDateForAPI = (date: Date | null, isEnd: boolean = false) => {
          if (!date) return undefined;
          if (isEnd) {
            return (
              moment(date).endOf("day").utc().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z"
            );
          }
          return (
            moment(date).startOf("day").utc().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z"
          );
        };

    // Prepare search data
    let searchData = {
      searchKey:
        data.keyword && data.keyword.trim() !== "" ? data.keyword : undefined,
      startDate: createdDateRange[0]
        ? formatDateForAPI(createdDateRange[0])
        : undefined,
      endDate: createdDateRange[1]
        ? formatDateForAPI(createdDateRange[1], true)
        : undefined,
      locationIds:
        data.locationIds?.map((loc: any) => Number(atob(loc.value))) || [],
      dateRangeType: activeTab === "Past" ? "Past" : activeTab === "Current and future" ? "CurrentAndFuture" : null,
    };
    
    // Check if we have any active filters
    const hasFilters = 
      (data.keyword && data.keyword.trim() !== "") ||
      !!createdDateRange[0] ||
      !!createdDateRange[1] ||
      (data.locationIds && data.locationIds.length > 0);
    
    setHasActiveFilters(hasFilters);
    
    console.log("Search Data:", searchData);
    // Only reset pagination if it's not from pagination change
    searchDiscounts(searchData, !fromPagination);
  };

  const closeAddDiscountModal = (success: boolean = false) => {
    setShowAddDiscountModal(false);
    setItemForEdit(null);
    if (success === true) {
      resetSearch();
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const handleTabChange = (tabName: string) => {
    setActiveTab(tabName);

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    switch (tabName) {
      case "Current and future":
        setCreatedDateRange([null, null]);
        setValue("createdDateFrom", null);
        setValue("createdDateTo", null);
        break;
      case "Past":
        setCreatedDateRange([null, null]);
        setValue("createdDateFrom", null);
        setValue("createdDateTo", null);
        break;
      case "All":
        setCreatedDateRange([null, null]);
        setValue("createdDateFrom", null);
        setValue("createdDateTo", null);
        break;
      default:
        break;
    }

    // Format date without timezone conversion
    
    const formatDateForAPI = (date: Date | null, isEnd: boolean = false) => {
          if (!date) return undefined;
          if (isEnd) {
            return (
              moment(date).endOf("day").utc().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z"
            );
          }
          return (
            moment(date).startOf("day").utc().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z"
          );
        };

    // Trigger search with new date range
    const searchData = {
      searchKey:
        getValues("keyword") && getValues("keyword").trim() !== ""
          ? getValues("keyword")
          : undefined,
      startDate: null,
      endDate: null,
      locationIds:
        getValues("locationIds")?.map((loc: any) => Number(atob(loc.value))) ||
        [],
      dateRangeType: tabName === "Past" ? "Past" : tabName === "Current and future" ? "CurrentAndFuture" : null,
    };

    // Check if we have any active filters after tab change
    const hasFilters = 
      (getValues("keyword") && getValues("keyword").trim() !== "") ||
      (tabName === "Current and future" || tabName === "Past") ||
      (getValues("locationIds") && getValues("locationIds").length > 0);
    
    setHasActiveFilters(hasFilters);

    searchDiscounts(searchData, true);
  };

  // Load required data for dropdowns using saga yieldPuts
  useEffect(() => {
    // Load locations - saga will automatically store in Redux
    commonApi.locationSearch(
      {},
      (res: any) => {
        if (!res?.success) {
          toast.error(res?.errorMessage || t("list.toasts.fetchLocationsFailed"));
          return;
        }
      },
      (error: any) => {
        console.log("Locations load error:", error);
      }
    );

    // Initialize with default tab
    handleTabChange("All");
  }, []); // Load once when component mounts

  useEffect(() => {
    // If we have active filters, get current form values and submit with them
    // Otherwise, do a basic search without filters
    if (hasActiveFilters && formRef.current) {
      // Use handleSubmit to get current form values and submit
      handleSubmit((data) => handleFormSubmit(data, true))();
    } else {
      // No active filters, do basic search
      searchDiscounts();
    }
  }, [currentPage, pageSize]);

  return (
    <Layout>
      <Header />
      <Permission value={permissionData} check="R" page={true}>
        <div className="flex-grow overflow-auto">
        <SubHeader title={t("list.title")} subTitle={t("list.subtitle")}>
          <Permission value={permissionData} check="W">
            <SiteButtons
              data={[
                {
                  text: t("list.buttons.addDiscount"),
                  click: () => {
                    setItemForEdit(null);
                    setShowAddDiscountModal(true);
                  },
                },
              ]}
            />
          </Permission>
        </SubHeader>
        <Container className="mb-12 opacity-100">
          <div className="my-4">
            <TopTabs
              tabs={tabs}
              activeTab={activeTab}
              setActiveTab={handleTabChange}
            />
          </div>
          {/* <BodyGroupHeading
            title="Search"
            className="flex items-center justify-between"
          /> */}
          <form ref={formRef} onSubmit={handleSubmit(onSubmit)} className="space-y-6 mb-6">
            {/* Search for discounts */}
            <div className="grid grid-cols-3 gap-4 my-6">
              <div>
                <Controller
                  name="keyword"
                  control={control}
                  rules={{
                    minLength: {
                      value: 3,
                      message: t("list.validation.searchMinLength"),
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      className="!my-0"
                      placeholder={t("list.placeholders.enterDiscountName")}
                      label={t("list.fields.searchForDiscounts")}
                      error={errors?.keyword?.message as any}
                    >
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 mt-1">
                        <Icon name="search" />
                      </span>
                    </Input>
                  )}
                />
              </div>

              {/* Date Range */}
              <div className="w-full !my-0">
                <style>
                  {`
                  .react-datepicker-wrapper,
                  .react-datepicker__input-container,
                  .react-datepicker__input-container input {
                    width: 100% !important;
                    display: block !important;
                  }
                  .react-datepicker__close-icon::after {
                    background-color: transparent !important;
                    color: #b6b5b5 !important;
                    font-size: 20px;
                  }
                  .react-datepicker__close-icon:hover::after {
                    background-color: transparent !important;
                    color: #b6b5b5 !important;
                    font-size: 20px;
                  }
                  `}
                </style>
                <p className="font-normal text-[0.938rem] leading-none mb-2">
                  {t("list.fields.dateRange")}
                </p>
                <DatePicker
                  selectsRange={true}
                  startDate={createdDateRange[0]}
                  endDate={createdDateRange[1]}
                  showYearDropdown
                  dropdownMode="select"
                  showMonthDropdown
                  onChange={(update: [Date | null, Date | null]) => {
                    // For Past tab: neither date can be today or future
                    if (activeTab === "Past") {
                      const yesterday = new Date();
                      yesterday.setDate(yesterday.getDate() - 1);
                      if (update[0] && update[0] > yesterday) {
                        toast.error(t("list.validation.pastStartDateInvalid"));
                        return;
                      }
                      if (update[1] && update[1] > yesterday) {
                        toast.error(t("list.validation.pastEndDateInvalid"));
                        return;
                      }
                    }
                    setCreatedDateRange(update);
                  }}
                  minDate={new Date("2020-01-01")}
                  maxDate={activeTab === "Past" ? (() => {
                    const yesterday = new Date();
                    yesterday.setDate(yesterday.getDate() - 1);
                    return yesterday;
                  })() : undefined}
                  dateFormat="MM/dd/yyyy"
                  className="w-full px-3 h-[2.875rem] py-[8px] border border-[#cecece] rounded-md focus:outline-none"
                  placeholderText={t("list.placeholders.selectDateRange")}
                  isClearable={true}
                />
              </div>

              {/* Location */}
              <div>
                <CommonDropdown
                  control={control}
                  setValue={setValue}
                  label={t("list.fields.location")}
                  name="locationIds"
                  className="!my-0"
                  type="allLocations"
                  placeholder={t("list.placeholders.selectLocation")}
                  isMulti={true}
                />
              </div>
            </div>
            <SiteButtons
              data={[
                { text: t("list.buttons.search"), type: "submit", working: searching, theme: "secondary" },
                { text: t("list.buttons.reset"), click: () => resetSearch(), theme: "secondary" },
              ]}
            />
          </form>
          <BodyGroupHeading
            subtitle={t("list.resultsFound", { count: totalRows })}
            showHr={false}
            className="flex items-center justify-between"
          />
          <ReusableTable
            key={`discount-table-${discountsData?.length || 0}`}
            data={discountsData}
            fixedHeight="calc(100vh - 200px)"
            columns={[
              {
                key: "discountName",
                label: t("list.columns.discountName"),
                sortable: true,
                width: "225px",
              },
              {
                key: "fkDiscountTypeLookupValueId",
                label: t("list.columns.type"),
                sortable: true,
                render: (item: any) => {
                  if (!item?.fkDiscountTypeLookupValueId) return "-";
                  const discountModeItem = lookup.getDiscountMode()?.find((lt: any) => {
                    // console.log("lt.lookupId", lt.lookupId);
                    // console.log(atob(item?.fkDiscountTypeLookupValueId));
                    return (
                      lt.lookupId == atob(item?.fkDiscountTypeLookupValueId)
                    );
                  });
                  return discountModeItem ? getLookupDisplayValue(discountModeItem, t) : "-";
                },
              },
              {
                key: "weeklySchedules",
                label: t("list.columns.recurring"),
                sortable: true,
                render: (item: any) => {
                  return (
                    item?.weeklySchedules
                      ?.map((schedule: any) => t(`list.days.${daysOfWeek[schedule.dayOfWeek]}`))
                      .join(", ") || "-"
                  );
                },
              },
              {
                key: "startDate",
                label: t("list.columns.startDate"),
                sortable: true,
                render: (item: any) => {
                  return item?.dateRanges?.length > 0
                    ? item?.dateRanges[0]?.startDate
                      ? <FormattedDate date={item.dateRanges[0].startDate} format="date"/>
                      : "-"
                    : "-";
                },
              },
              {
                key: "endDate",
                label: t("list.columns.endDate"),
                sortable: true,
                render: (item: any) => {
                  return item?.dateRanges?.length > 0
                    ? item?.dateRanges[0]?.endDate
                      ? <FormattedDate date={item.dateRanges[0].endDate} format="date"/>
                      : "-"
                    : "-";
                },
              },
              {
                key: "customerGroups",
                label: t("list.columns.customerGroups"),
                sortable: false,
                render: (item: any) => {
                  return item?.customerGroups && item.customerGroups.length > 0
                    ? (
                      <ul className="list-disc list-inside">
                        {item.customerGroups.map((cg: any, idx: number) => (
                        <li key={idx}>{getCustomerGroupDisplayName(cg, t)}</li>
                        ))}
                      </ul>
                      )
                    : t("list.values.allCustomerGroups");
                },
              }
              //   { key: "customerGroup", label: "Customer Group" },
            ]}
            enableExpand={false}
            enableDrag={false}
            isRearranging={false}
            getRowActions={(discount: any) => (
              <>
                <Permission value={permissionData} check="W">
                  <button 
                    className="p-1 text-secondary hover:bg-cyan-50 rounded"
                    onClick={() => handleEdit(discount)}
                    title={t("common.actions.edit")}
                  >
                    <Icon name="edit" className="w-4 h-4" />
                  </button>
                </Permission>
                <Permission value={permissionData} check="D">
                  <button
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                    title={t("common.actions.delete")}
                    onClick={() =>
                      confirm({
                        type: "delete",
                        title: t("list.deleteConfirm.title"),
                        message: t("list.deleteConfirm.message", {
                          name: discount.discountName,
                        }),
                        onConfirm: () => confirmDelete(discount),
                      })
                    }
                  >
                    <Icon name="delete" className="w-4 h-4" />
                  </button>
                </Permission>
              </>
            )}
          />
          {totalRows > 0 && (
            <Pagination
              currentPage={currentPage}
              totalRecords={totalRows}
              perPage={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </Container>
        {showAddDiscountModal && (
          <SiteModal
            isOpen={showAddDiscountModal}
            onClose={closeAddDiscountModal}
            title={t("list.modal.title")}
          >
            <div className="p-3">
              {[
                {
                  label: t("list.modal.productDiscount.label"),
                  description: t("list.modal.productDiscount.description"),
                  value: 'product-discount',
                  disable: false,
                },
                {
                  label: t("list.modal.buyXGetY.label"),
                  description: t("list.modal.buyXGetY.description"),
                  value: btoa(`136`),
                  disable: false,
                },
                {
                  label: t("list.modal.orderDiscount.label"),
                  description: t("list.modal.orderDiscount.description"),
                  value: btoa(`137`),
                  disable: false,
                },
                {
                  label: t("list.modal.shippingDiscount.label"),
                  description: t("list.modal.shippingDiscount.description"),
                  value: btoa(`138`),
                  disable: true,
                },
              ].map((type: any) => (
                <button
                  key={type.value}
                  disabled={type.disable}
                  className={`w-full px-2 ${
                    !type.disable ? "hover:bg-gray-200" : " opacity-70"
                  } overflow-hidden rounded-md flex items-center justify-between py-3 border-b last:border-0`}
                  onClick={() =>
                    navigate(URLS.CREATE_DISCOUNT(type.value.toLowerCase()))
                  }
                >
                  <p className={`grow text-left`}>
                    <span className={`block text-black`}>{type.label}</span>
                    <span className="block text-sm text-gray-500 text-left">
                      {type.description}
                    </span>
                  </p>
                  <Icon name="arrowright" />
                </button>
              ))}
            </div>
          </SiteModal>
        )}
        </div>
      </Permission>
    </Layout>
  );
};

export default Discounts;